import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

/**
 * Applies the theme with transitions muted, so the swap is instant rather than
 * a colour smear, and so transitioned properties land on the new value rather
 * than somewhere along the way.
 */
function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.add('theme-switching');
  root.setAttribute('data-theme', theme);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'light' ? '#f4f6fa' : '#0a0a0a');
  // Two frames: one for the class to take effect, one for the paint.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => root.classList.remove('theme-switching')),
  );
}

/** Kept in sync with the pre-paint script in public/index.html. */
const STORAGE_KEY = 'karting-theme';
const LIGHT_QUERY = '(prefers-color-scheme: light)';

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}

/** What the user chose, or 'system' if they never did. */
export function storedPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : 'system';
  } catch {
    // Safari in private mode throws on localStorage rather than returning null.
    return 'system';
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

/**
 * Owns the light/dark choice.
 *
 * `data-theme` is already on <html> before React mounts — index.html sets it
 * synchronously so there is no flash of the wrong theme. This hook keeps that
 * attribute in step afterwards, and re-resolves when the OS flips, but only
 * while the preference is still 'system'.
 */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(storedPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(storedPreference()));

  useEffect(() => {
    const next = resolveTheme(preference);
    setResolved(next);
    applyTheme(next);

    try {
      if (preference === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Not being able to persist is not a reason to fail the switch.
    }
  }, [preference]);

  // Follow the OS while the user has expressed no preference of their own.
  useEffect(() => {
    if (preference !== 'system') return;
    if (typeof window.matchMedia !== 'function') return;

    const query = window.matchMedia(LIGHT_QUERY);
    const onChange = () => {
      const next = systemTheme();
      setResolved(next);
      applyTheme(next);
    };

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    // Safari only grew addEventListener on MediaQueryList in 14.
    if (typeof query.addListener === 'function') {
      query.addListener(onChange);
      return () => query.removeListener(onChange);
    }
  }, [preference]);

  /** Flips to the opposite of what is on screen, pinning the choice. */
  const toggle = useCallback(() => {
    setPreference(resolveTheme(storedPreference()) === 'light' ? 'dark' : 'light');
  }, []);

  return { preference, resolved, setPreference, toggle };
}
